import {Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Box} from '@mui/system';
import {HandoverFooterNavigation} from '../../components/FooterNavigation/HandoverFooterNavigation';
import {GoalsGrid} from '../../features/goals/GoalsGrid/GoalsGrid';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import TasksGrid from '../../features/tasks/TasksGrid/TasksGrid';
import {useReviewContext} from '../../contexts/ReviewContext';
import {RecommendsGrid} from '../../features/recommendations/RecommendsGrid/RecommendsGrid';
import {BuilderGrid} from '../../features/builder/BuilderGrid/BuilderGrid';
import useTitle from '../../hooks/useTitle';
import {DocumentModalInner} from '../../components/DocumentGenerator/DocumentGenerator';
import {PrecursorSummaryInner} from '../PrecursorSummaryPage/PrecursorSummaryPage';
import {HandoverNotes} from '../HandoverNotesPage/HandoverNotes';
import {SummarySection} from './SummarySection';
import {HandoverSubmitModal} from './HandoverSubmitModal';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {
  GlobalPermissionEnum,
  ReviewStatusOneviewEnum,
} from '../../schema-types/globalTypes';
import {PostcursorSummary} from '../PostcursorPage/PostcursorSummary';
import {FeesBreakdown} from '../../components/FeesBreakdown/FeesBreakdown';
import DisabledWrapper from '../../components/DisabledWrapper/DisabledWrapper';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import {LoadAllDummyDataButton} from '../../components/LoadAllDummyDataButton';

const HandoverSummaryInner: React.FC = () => {
  const {
    reviewDetails,
    reviewValidation: review,
    refreshReview,
    reviewEditable,
  } = useReviewContext();
  const {accountId} = useParams();
  const [handoverSubmitOpen, setHandoverSubmitOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isDummyDataLoading, setIsDummyDataLoading] = useState(false);

  useEffect(() => {
    refreshReview(); // get latest validation
  }, []);

  if (!accountId) {
    return <div>Something went wrong</div>;
  }

  const editable = reviewDetails?.isDraft || false;
  const complete =
    review?.statusOneview === ReviewStatusOneviewEnum.CLOSED ||
    review?.statusOneview === ReviewStatusOneviewEnum.COMPLETED ||
    review?.statusOneview === ReviewStatusOneviewEnum.SOFT_SUBMITTED;

  const canCreateDummyData = editable && !isDummyDataLoading;

  return (
    <>
      <HandoverStep />

      {/* Dummy Data Button */}
      <FeatureBoundary
        features={[GlobalPermissionEnum.can_access_admin_features]}
        showErrorIfDenied={false}
      >
        <Box sx={{mb: 2, display: 'flex', justifyContent: 'flex-end'}}>
          <LoadAllDummyDataButton
            readOnly={!canCreateDummyData}
            incrementRefreshKey={() => setRefreshKey((prev) => prev + 1)}
            onCompleted={() => {}}
          />
        </Box>
      </FeatureBoundary>

      <PrecursorSummaryInner showFooter={false} />
      <PostcursorSummary showFooter={false} />
      <Box mt={4} />
      <SummarySection editable={editable} step="notes" title="Soft notes">
        <HandoverNotes
          key={refreshKey}
          reviewId={review?.id || reviewDetails?.id}
          readOnly
        />
      </SummarySection>
      <SummarySection editable={editable} step="goals" title="Goals">
        <GoalsGrid minHeight={220} accountId={accountId} readOnly />
      </SummarySection>
      <DisabledWrapper>
        <SummarySection editable={editable} step="requirements" title="Requirements">
          <RecommendsGrid minHeight={200} readOnly entityId={accountId} />
        </SummarySection>
      </DisabledWrapper>
      <DisabledWrapper>
        <SummarySection editable={editable} step="requirements" title="Builder">
          <BuilderGrid minHeight={200} readOnly entityId={accountId} />
        </SummarySection>
      </DisabledWrapper>
      <SummarySection
        editable={false} // this is handled by the BE for this component
        step="tasks" // same as above
        title="Fees Breakdown"
      >
        <ErrorBoundary inline>
          <FeesBreakdown />
        </ErrorBoundary>
      </SummarySection>
      <SummarySection editable={editable} step="tasks" title="Tasks">
        <TasksGrid minHeight={200} readOnly />
      </SummarySection>
      {complete ? (
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_documents]}
          showErrorIfDenied={false}
        >
          <SummarySection editable={editable} step="tasks" title="Generate Documents">
            {!review?.curoId || review.isDraft ? (
              <Typography>
                You can only generate documents once you have submitted
              </Typography>
            ) : null}
            <DocumentModalInner
              entityId={review?.curoId}
              entity="t4a_review"
              disabled={!review?.curoId || review.isDraft || false}
            />
          </SummarySection>
        </FeatureBoundary>
      ) : null}
      <HandoverFooterNavigation
        handleSave={() => {
          setHandoverSubmitOpen(true);
        }}
      />
      <HandoverSubmitModal
        isOpen={handoverSubmitOpen}
        close={() => setHandoverSubmitOpen(false)}
      />
    </>
  );
};

export const HandoverSummaryPage: React.FC = () => {
  const {reviewDetails} = useReviewContext();
  useTitle(`Review summary - ${reviewDetails?.account?.name}`);

  return <HandoverSummaryInner />;
};
